import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Check, Zap, Star, Crown, ChevronDown, ChevronUp, X, Share, History } from 'lucide-react';
import ProfilePicture from './ProfilePicture';
import DarkModeToggle from './ui/dark-mode-toggle';
import { useDarkMode } from './DarkModeContext';
import { doc, updateDoc, getDoc, collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { trackEvent, trackBeginCheckout } from '../lib/analytics';

const PricingPage = ({ user, onClose, onPurchase, mockupCount, onLogout, onSignIn, fromLandingPage = false, onShowCookiePreferences, onShowUpdates, onShowTerms, onShowPrivacy, showSuccessModal, setShowSuccessModal, successModalData, setSuccessModalData, addMockups }) => {
  const { isDarkMode } = useDarkMode();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [openFAQ, setOpenFAQ] = useState('credits');
  const [billingPeriod, setBillingPeriod] = useState('one-time');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [hasSharedToX, setHasSharedToX] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showCreditHistory, setShowCreditHistory] = useState(false);
  const [creditHistory, setCreditHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Check if user has already shared to X
  const checkShareStatus = useCallback(async () => {
    if (!user || !db) return;
    
    try {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        setHasSharedToX(userData.hasSharedToX || false);
      }
    } catch (error) {
      console.error('Error checking share status:', error);
    }
  }, [user]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check share status when user changes
  useEffect(() => {
    if (user) {
      checkShareStatus();
    }
  }, [user, checkShareStatus]);

  // Fetch credit purchase history
  const fetchCreditHistory = useCallback(async () => {
    if (!user || !db) {
      console.warn('⚠️ Cannot fetch credit history: missing user or db', { user: !!user, db: !!db });
      return;
    }
    
    console.log('🔍 Fetching credit history for user:', user.uid);
    console.log('🔍 User object:', { uid: user.uid, email: user.email });
    setLoadingHistory(true);
    try {
      const purchasesRef = collection(db, 'creditPurchases');
      
      // Always start with simple query (no orderBy) to avoid index issues
      // This is more reliable and we can sort in memory
      let querySnapshot;
      try {
        const q = query(
          purchasesRef,
          where('userId', '==', user.uid)
        );
        console.log('🔍 Executing query with userId:', user.uid);
        querySnapshot = await getDocs(q);
        console.log('✅ Credit history query successful (simple query)');
        console.log('✅ Query snapshot:', {
          size: querySnapshot.size,
          empty: querySnapshot.empty,
          docs: querySnapshot.docs.length
        });
      } catch (queryError) {
        console.error('❌ Query failed:', queryError);
        console.error('Query error details:', {
          code: queryError.code,
          message: queryError.message,
          stack: queryError.stack
        });
        throw queryError;
      }
      
      console.log(`📊 Query returned ${querySnapshot.size} documents`);
      console.log(`📊 Searching for userId: "${user.uid}"`);
      
      // If no documents, try fetching all to debug
      if (querySnapshot.empty) {
        console.warn('⚠️ Query returned empty! Trying to fetch all documents for debugging...');
        try {
          const allDocs = await getDocs(purchasesRef);
          console.log(`📊 Total documents in collection: ${allDocs.size}`);
          allDocs.forEach((doc) => {
            const data = doc.data();
            console.log('📄 All document:', doc.id, {
              userId: data.userId,
              queryUserId: user.uid,
              match: data.userId === user.uid,
              credits: data.credits
            });
          });
        } catch (debugError) {
          console.error('❌ Debug query failed:', debugError);
        }
      }
      
      const purchases = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('📄 Purchase document:', doc.id, {
          documentUserId: data.userId,
          queryUserId: user.uid,
          userIdMatch: data.userId === user.uid,
          credits: data.credits,
          amount: data.amount,
          createdAt: data.createdAt,
          purchaseDate: data.purchaseDate
        });
        
        // Verify userId matches (should always match due to query, but double-check)
        if (data.userId !== user.uid) {
          console.warn('⚠️ UserId mismatch!', {
            documentUserId: data.userId,
            queryUserId: user.uid
          });
          return; // Skip this document
        }
        
        // Handle date conversion - createdAt might be a serverTimestamp that hasn't resolved yet
        let purchaseDate = null;
        if (data.purchaseDate) {
          purchaseDate = data.purchaseDate?.toDate ? data.purchaseDate.toDate() : data.purchaseDate;
        } else if (data.createdAt) {
          purchaseDate = data.createdAt?.toDate ? data.createdAt.toDate() : 
                         (data.createdAt?.seconds ? new Date(data.createdAt.seconds * 1000) : 
                         (typeof data.createdAt === 'string' ? new Date(data.createdAt) : new Date()));
        } else {
          purchaseDate = new Date(); // Fallback to now
        }
        
        purchases.push({
          id: doc.id,
          ...data,
          purchaseDate: purchaseDate
        });
      });
      
      // Sort by date descending if we didn't use orderBy
      purchases.sort((a, b) => {
        const dateA = a.purchaseDate || new Date(0);
        const dateB = b.purchaseDate || new Date(0);
        return dateB.getTime() - dateA.getTime();
      });
      
      console.log(`✅ Loaded ${purchases.length} credit purchases:`, purchases.map(p => ({
        id: p.id,
        credits: p.credits,
        amount: p.amount,
        date: p.purchaseDate
      })));
      
      console.log('📦 Setting credit history state with', purchases.length, 'purchases');
      setCreditHistory(purchases);
      console.log('✅ Credit history state updated');
    } catch (error) {
      console.error('❌ Error fetching credit history:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack,
        userId: user?.uid
      });
      
      // Show user-friendly error message
      if (error.message && (error.message.includes('index') || error.code === 'failed-precondition')) {
        console.warn('⚠️ Firestore index required. Purchase history may be incomplete.');
        // Still try to show what we can
        setCreditHistory([]);
      } else {
        setCreditHistory([]);
      }
    } finally {
      setLoadingHistory(false);
    }
  }, [user]);

  // Auto-refresh credit history when modal opens
  useEffect(() => {
    if (showCreditHistory && user) {
      console.log('🔄 Modal opened, fetching credit history...');
      fetchCreditHistory();
      
      // Also set up a retry mechanism in case webhook is still processing
      const retryTimer = setTimeout(() => {
        console.log('🔄 Retrying credit history fetch after 3 seconds...');
        fetchCreditHistory();
      }, 3000);
      
      return () => clearTimeout(retryTimer);
    }
  }, [showCreditHistory, user, fetchCreditHistory]);

  // Set up real-time listener for credit purchases when modal is open
  useEffect(() => {
    if (!showCreditHistory || !user || !db) {
      return;
    }

    console.log('👂 Setting up real-time listener for credit purchases...');
    const purchasesRef = collection(db, 'creditPurchases');
    const q = query(
      purchasesRef,
      where('userId', '==', user.uid)
    );

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        console.log('📡 Real-time update received:', querySnapshot.size, 'documents');
        
        const purchases = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          
          // Verify userId matches
          if (data.userId !== user.uid) {
            return; // Skip this document
          }
          
          // Handle date conversion
          let purchaseDate = null;
          if (data.purchaseDate) {
            purchaseDate = data.purchaseDate?.toDate ? data.purchaseDate.toDate() : data.purchaseDate;
          } else if (data.createdAt) {
            purchaseDate = data.createdAt?.toDate ? data.createdAt.toDate() : 
                           (data.createdAt?.seconds ? new Date(data.createdAt.seconds * 1000) : 
                           (typeof data.createdAt === 'string' ? new Date(data.createdAt) : new Date()));
          } else {
            purchaseDate = new Date(); // Fallback to now
          }
          
          purchases.push({
            id: doc.id,
            ...data,
            purchaseDate: purchaseDate
          });
        });
        
        // Sort by date descending
        purchases.sort((a, b) => {
          const dateA = a.purchaseDate || new Date(0);
          const dateB = b.purchaseDate || new Date(0);
          return dateB.getTime() - dateA.getTime();
        });
        
        console.log(`✅ Real-time update: ${purchases.length} purchases`);
        setCreditHistory(purchases);
        setLoadingHistory(false);
      },
      (error) => {
        console.error('❌ Real-time listener error:', error);
        // Fall back to manual fetch on error
        fetchCreditHistory();
      }
    );

    return () => {
      console.log('🔇 Cleaning up real-time listener');
      unsubscribe();
    };
  }, [showCreditHistory, user, fetchCreditHistory]);

  // Debug: Log when creditHistory changes
  useEffect(() => {
    console.log('📊 Credit history state changed:', {
      length: creditHistory.length,
      purchases: creditHistory.map(p => ({ id: p.id, credits: p.credits, amount: p.amount }))
    });
  }, [creditHistory]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.dropdown-container')) {
        setShowUserDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  // Helper function to format price without trailing .00
  const formatPrice = (price) => {
    // Remove trailing zeros and decimal point if not needed
    return parseFloat(price.toFixed(2)).toString();
  };

  // Calculate discounted prices based on billing period
  const calculatePrice = (originalPrice) => {
    const price = parseFloat(originalPrice.replace('€', ''));
    let calculatedPrice;
    switch (billingPeriod) {
      case 'monthly':
        calculatedPrice = price * 0.8; // 20% off monthly
        break;
      case 'annually':
        calculatedPrice = price * 12 * 0.6; // 40% off but for 12 months upfront
        break;
      default:
        calculatedPrice = price;
    }
    return formatPrice(calculatedPrice);
  };

  const getBillingDescription = () => {
    switch (billingPeriod) {
      case 'monthly':
        return 'per month';
      case 'annually':
        return 'per year (12 months)';
      default:
        return 'One-time';
    }
  };

  // Determine which plan should be "Most Popular" based on billing period
  const isPlanPopular = (plan) => {
    switch (billingPeriod) {
      case 'one-time':
        return plan.id === 'premium-pack'; // 200 Credits for one-time
      case 'monthly':
        return plan.id === 'premium-pack'; // 200 Credits for monthly (current)
      case 'annually':
        return plan.id === 'medium-pack'; // 15 Credits * 12 = 180 Credits for annual
      default:
        return plan.popular; // fallback to original value
    }
  };

  const buyPacks = [
    {
      id: 'small-pack',
      name: '5 Credits',
      headline: 'Starter',
      price: '€2',
      period: 'One-time',
      mockups: 5,
      costPerMockup: '€0.40',
      description: 'Perfect for trying out',
      popular: false,
      icon: <Star className="w-6 h-6" />,
      buttonText: 'Buy Now',
      disabled: false
    },
    {
      id: 'medium-pack',
      name: '15 Credits',
      headline: 'Essentials',
      price: '€5',
      period: 'One-time',
      mockups: 15,
      costPerMockup: '€0.33',
      description: 'Great value for regular users',
      popular: false,
      icon: <Crown className="w-6 h-6" />,
      buttonText: 'Buy Now',
      disabled: false
    },
    {
      id: 'big-pack',
      name: '50 Credits',
      headline: 'Professional',
      price: '€10',
      period: 'One-time',
      mockups: 50,
      costPerMockup: '€0.20',
      description: 'Best value for professionals',
      popular: false,
      icon: <Zap className="w-6 h-6" />,
      buttonText: 'Buy Now',
      disabled: false
    },
    {
      id: 'premium-pack',
      name: '200 Credits',
      headline: 'Premium',
      price: '€20',
      period: 'One-time',
      mockups: 200,
      costPerMockup: '€0.10',
      description: 'Most popular choice',
      popular: true,
      icon: <Crown className="w-6 h-6" />,
      buttonText: 'Buy Now',
      disabled: false
    },
    {
      id: 'enterprise-pack',
      name: '800 Credits',
      headline: 'Enterprise',
      price: '€40',
      period: 'One-time',
      mockups: 800,
      costPerMockup: '€0.05',
      description: 'Best value for agencies',
      popular: false,
      icon: <Zap className="w-6 h-6" />,
      buttonText: 'Buy Now',
      disabled: false
    }
  ];


  const handlePurchase = async (plan) => {
    console.log('PricingPage handlePurchase called with plan:', plan.id, 'loading:', loading);
    
    if (loading) {
      console.log('Already processing, skipping duplicate call');
      return;
    }
    
    // Check if user is authenticated
    if (!user) {
      if (onSignIn) {
        onSignIn();
      } else {
        alert('Please sign in to purchase credits.');
      }
      return;
    }
    
    setLoading(true);
    setSelectedPlan(plan.id);
    
    // Track begin checkout event
    trackBeginCheckout({
      items: [{
        item_id: plan.id,
        item_name: plan.name,
        price: parseFloat(plan.price.replace('€', '')),
        quantity: 1
      }],
      value: parseFloat(plan.price.replace('€', '')),
      currency: 'EUR'
    });
    
    try {
      console.log('Calling parent onPurchase with plan:', plan.id, 'billingPeriod:', billingPeriod);
      await onPurchase(plan, billingPeriod);
      
      // Note: onPurchase redirects to Stripe checkout, so we don't show success modal here
      // Success will be handled by Stripe webhook and success page
      console.log('Redirecting to Stripe checkout...');
      
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Purchase failed. Please try again.');
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header with user profile */}
      <div className="bg-surface-1/80 backdrop-blur-md flex-shrink-0 sticky top-0 z-50">
        <div className="w-full pl-6 pr-4 sm:pl-6 sm:pr-6 lg:pl-6 lg:pr-6">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center">
              <img 
                src={isDarkMode ? "/RandomMockups-Logo-LightMode.png" : "/RandomMockups-Logo.png"} 
                alt="RandomMockups Logo" 
                className="mr-1.5"
                style={{ height: '40px', width: 'auto' }}
              />
              <h1 
                className="text-xl font-semibold text-foreground cursor-pointer hover:text-muted-foreground transition-colors"
                onClick={onClose}
              >
                RandomMockups
              </h1>
            </div>
            <div className="flex items-center space-x-3">
              <DarkModeToggle />
              {!user && (
                <Button 
                  onClick={onSignIn}
                  variant="outline"
                  size="sm"
                  className="text-foreground hover:text-foreground bg-surface-2 hover:bg-muted"
                >
                  Sign in
                </Button>
              )}
              {user && (
                <button
                  onClick={() => {
                    // Already on pricing page, no action needed
                  }}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:bg-muted transition-colors p-2 rounded-lg"
                >
                  <ProfilePicture user={user} className="w-6 h-6 rounded-full" />
                  <span className={`bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center ${
                    mockupCount >= 10 
                      ? 'rounded-full px-2 py-1 min-w-8 h-6' 
                      : 'rounded-full w-5 h-5'
                  }`}>
                    {mockupCount}
                  </span>
                </button>
              )}
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="py-8 flex-1 flex items-center">
        <div className="max-w-[1480px] mx-auto px-4 sm:px-6 lg:px-8 w-full">
          {/* Page Header */}
          <div className="mb-12">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h1 className="text-4xl md:text-5xl font-bold text-foreground flex items-center gap-3">
                {user ? (
                  <>
                    Remaining credits: 
                    <span className={`bg-primary text-primary-foreground text-4xl md:text-5xl font-semibold flex items-center justify-center ${
                      mockupCount >= 10 
                        ? 'rounded-full px-8 py-4 min-w-20 h-16' 
                        : 'rounded-full w-16 h-16'
                    }`}>
                      {mockupCount}
                    </span>
                  </>
                ) : (
                  <>3 Credits available for free</>
                )}
              </h1>
              
              {/* Billing Period Segmented Control */}
              <div className="flex bg-muted dark:bg-muted bg-white border border-border rounded-lg p-1 shadow-sm">
                <button
                  onClick={() => {
                    trackEvent('select_billing_period', {
                      period: 'freebies'
                    });
                    setBillingPeriod('freebies');
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    billingPeriod === 'freebies'
                      ? 'bg-background dark:bg-background bg-gray-50 text-foreground shadow-sm border border-border'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Freebies
                </button>
                <button
                  onClick={() => {
                    trackEvent('select_billing_period', {
                      period: 'one-time'
                    });
                    setBillingPeriod('one-time');
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    billingPeriod === 'one-time'
                      ? 'bg-background dark:bg-background bg-gray-50 text-foreground shadow-sm border border-border'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  One time
                </button>
                <button
                  onClick={() => {
                    trackEvent('select_billing_period', {
                      period: 'monthly'
                    });
                    setBillingPeriod('monthly');
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    billingPeriod === 'monthly'
                      ? 'bg-background dark:bg-background bg-gray-50 text-foreground shadow-sm border border-border'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Monthly (20% off)
                </button>
                <button
                  onClick={() => {
                    trackEvent('select_billing_period', {
                      period: 'annually'
                    });
                    setBillingPeriod('annually');
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    billingPeriod === 'annually'
                      ? 'bg-background dark:bg-background bg-gray-50 text-foreground shadow-sm border border-border'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Annually (40% off)
                </button>
              </div>
            </div>
          </div>

          {/* Plans Layout */}
          <div className="mb-16">
            {/* Freebies section */}
            {billingPeriod === 'freebies' && (
              <div className="mb-8">
                <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-6 w-full max-w-md mx-auto">
                  <Card className="relative transition-all duration-200 hover:shadow-lg">
                    <CardHeader className="pb-4">
                      <div className="text-center">
                        <div className="flex justify-center mb-2">
                          <Share className="w-6 h-6 text-primary" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                          Share to X
                        </h3>
                        <h1 className="text-5xl font-bold text-foreground">
                          5 Credits
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                          Earn credits by sharing
                        </p>
                      </div>
                    </CardHeader>

                    <CardContent className="pt-6">
                      <ul className="space-y-3 mb-6">
                        <li className="flex items-start">
                          <Check className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-foreground font-bold">5 credits for sharing to X</span>
                        </li>
                        <li className="flex items-start">
                          <Check className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-foreground">Credits never expire</span>
                        </li>
                        <li className="flex items-start">
                          <Check className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-foreground">Share your mockups</span>
                        </li>
                      </ul>

                      <Button
                        onClick={() => {
                          // Check if user has already shared
                          if (hasSharedToX) {
                            trackEvent('share_for_credits', {
                              completed: false,
                              reason: 'already_shared'
                            });
                            const modalData = {
                              credits: 0,
                              headline: "Already shared!",
                              text: "You've already earned credits for sharing. Thanks for your support!"
                            };
                            setSuccessModalData(modalData);
                            setTimeout(() => {
                              setShowSuccessModal(true);
                            }, 100);
                            return;
                          }

                          // Track share attempt
                          trackEvent('share_for_credits', {
                            completed: false,
                            reason: 'initiated'
                          });

                          // Handle share to X functionality
                          const shareText = "Check out my amazing mockup created with RandomMockups! 🎨";
                          const shareUrl = window.location.origin;
                          const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
                          
                          // Open Twitter/X
                          window.open(twitterUrl, '_blank', 'width=550,height=420');
                          
                          // Show verification modal after a short delay
                          setTimeout(() => {
                            setShowVerificationModal(true);
                          }, 1000);
                        }}
                        className={`lickable-btn w-full h-12 !text-base font-semibold ${
                          hasSharedToX ? 'opacity-75 cursor-not-allowed' : ''
                        }`}
                        disabled={hasSharedToX}
                      >
                        {hasSharedToX ? 'Credits Earned!' : 'Share to X'}
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Credit packs section */}
            {billingPeriod !== 'freebies' && (
              <div className="mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
                  {buyPacks.filter(plan => plan.id !== 'small-pack').map((plan) => (
                  <Card 
                    key={plan.id} 
                    className={`relative transition-all duration-200 hover:shadow-lg ${
                      isPlanPopular(plan)
                        ? 'ring-2 ring-blue-500 shadow-lg' 
                        : ''
                    }`}
                  >
                    {isPlanPopular(plan) && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <Badge 
                          className="bg-primary text-primary-foreground px-3 py-1" 
                          style={{ pointerEvents: 'none' }}
                        >
                          Most Popular
                        </Badge>
                      </div>
                    )}
                    
                    <CardHeader className="pb-4">
                      <div className="text-left">
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                          {plan.headline}
                        </h3>
                        <div className="flex items-baseline gap-2">
                          <h1 className="text-5xl font-bold text-foreground">
                            €{calculatePrice(plan.price)}
                          </h1>
                          <p className="text-sm text-muted-foreground">
                            {getBillingDescription()}
                          </p>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="pt-6">
                      <ul className="space-y-3 mb-6">
                        <li className="flex items-start">
                          <Check className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-foreground font-bold">
                            {billingPeriod === 'annually' 
                              ? `Generate ${plan.mockups * 12} mockups`
                              : `Generate ${plan.mockups} mockups`
                            }
                          </span>
                        </li>
                        <li className="flex items-start">
                          <Check className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-foreground font-bold">€{(() => {
                            const originalPrice = parseFloat(plan.price.replace('€', ''));
                            const mockups = plan.mockups;
                            let pricePerMockup;
                            if (billingPeriod === 'annually') {
                              // For annual, use the monthly equivalent price (with 40% discount)
                              const monthlyPrice = originalPrice * 0.6; // 40% off
                              pricePerMockup = monthlyPrice / mockups;
                            } else {
                              // For one-time and monthly, use normal calculation
                              const price = parseFloat(calculatePrice(plan.price));
                              pricePerMockup = price / mockups;
                            }
                            // Format without trailing zeros
                            return parseFloat(pricePerMockup.toFixed(3)).toString();
                          })()} per mockup</span>
                        </li>
                        <li className="flex items-start">
                          <Check className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-foreground">All device types</span>
                        </li>
                        <li className="flex items-start">
                          <Check className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-foreground">All style categories</span>
                        </li>
                        <li className="flex items-start">
                          <Check className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-foreground">Credits never expire</span>
                        </li>
                      </ul>

                      <Button
                        onClick={() => handlePurchase(plan)}
                        disabled={plan.disabled || loading}
                        className={`lickable-btn w-full h-12 !text-base font-semibold ${
                          plan.disabled
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }`}
                      >
                        {loading && selectedPlan === plan.id ? (
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></div>
                            <span>Processing...</span>
                          </div>
                        ) : (
                          plan.buttonText
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
            )}
          </div>

          {/* Previously Purchased Credits Section - Temporarily hidden */}
          {false && user && billingPeriod !== 'freebies' && (
            <div className="mb-12 flex items-center justify-center gap-4">
              <span className="text-muted-foreground">Previously purchased credits:</span>
              <Button
                onClick={async () => {
                  setShowCreditHistory(true);
                  // Always refresh when opening the modal to get latest purchases
                  await fetchCreditHistory();
                }}
                variant="outline"
                className="flex items-center gap-2"
              >
                <History className="w-4 h-4" />
                See Credit History
              </Button>
            </div>
          )}

          {/* FAQ and Credit Usage Section */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* FAQ Section - 3/5 width */}
            <div className="lg:col-span-3">
              <h2 className="text-3xl font-bold text-foreground mb-8">
                Frequently Asked Questions
              </h2>
              
              <div className="space-y-6">
                <div>
                  <button
                    onClick={() => setOpenFAQ(openFAQ === 'credits' ? null : 'credits')}
                    className="w-full text-left flex items-center justify-between py-4 hover:bg-muted/50 transition-colors"
                  >
                    <h3 className="text-lg font-semibold text-foreground">
                      How do credits work?
                    </h3>
                    {openFAQ === 'credits' ? (
                      <ChevronUp className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    )}
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openFAQ === 'credits' ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="py-4">
                      <p className="text-muted-foreground">
                        Each time you generate a mockup, one credit is deducted from your allowance.<br/>
                        You can generate as many mockups as you have credits remaining.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <button
                    onClick={() => setOpenFAQ(openFAQ === 'runout' ? null : 'runout')}
                    className="w-full text-left flex items-center justify-between py-4 hover:bg-muted/50 transition-colors"
                  >
                    <h3 className="text-lg font-semibold text-foreground">
                      What happens when I run out of credits?
                    </h3>
                    {openFAQ === 'runout' ? (
                      <ChevronUp className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    )}
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openFAQ === 'runout' ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="py-4">
                      <p className="text-muted-foreground">
                        When you run out of credits, you'll need to purchase more or upgrade your plan.<br/>
                        You can buy individual packs or subscribe to a monthly plan for ongoing access.
                      </p>
                    </div>
                  </div>
                </div>


                <div>
                  <button
                    onClick={() => setOpenFAQ(openFAQ === 'rollover' ? null : 'rollover')}
                    className="w-full text-left flex items-center justify-between py-4 hover:bg-muted/50 transition-colors"
                  >
                    <h3 className="text-lg font-semibold text-foreground">
                      Do unused credits roll over?
                    </h3>
                    {openFAQ === 'rollover' ? (
                      <ChevronUp className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    )}
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openFAQ === 'rollover' ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="py-4">
                      <p className="text-muted-foreground">
                        For one-time purchases, credits never expire. For monthly subscriptions,<br/>
                        unused credits do not roll over to the next month.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <button
                    onClick={() => setOpenFAQ(openFAQ === 'included' ? null : 'included')}
                    className="w-full text-left flex items-center justify-between py-4 hover:bg-muted/50 transition-colors"
                  >
                    <h3 className="text-lg font-semibold text-foreground">
                      What's included in all packages?
                    </h3>
                    {openFAQ === 'included' ? (
                      <ChevronUp className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    )}
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openFAQ === 'included' ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="py-4">
                      <p className="text-muted-foreground">
                        All plans include access to all device types (mobile, tablet, desktop, ultrawide) and all style categories<br/>
                        (simple, office, nature, etc.). The only difference is the number of credits you can use to generate mockups.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Credit Usage Section - 2/5 width */}
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-foreground mb-8">
                Credit usage
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between py-4">
                  <h3 className="text-lg font-semibold text-foreground">Mockup generation</h3>
                  <span className="text-lg text-muted-foreground">1 credit</span>
                </div>
                
                <div className="flex items-center justify-between py-4">
                  <h3 className="text-lg font-semibold text-foreground">Mockup Upscale 2K</h3>
                  <span className="text-lg text-muted-foreground">1 credit</span>
                </div>
                
                <div className="flex items-center justify-between py-4">
                  <h3 className="text-lg font-semibold text-foreground">Mockup Upscale 4K</h3>
                  <span className="text-lg text-muted-foreground">2 credits</span>
                </div>
                
                <div className="flex items-center justify-between py-4">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-foreground line-through">Mockup Edit</h3>
                    <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded">Coming</span>
                  </div>
                  <span className="text-lg text-muted-foreground">1 credit</span>
                </div>
                
                <div className="flex items-center justify-between py-4">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-foreground line-through">Mockup Video</h3>
                    <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded">Coming</span>
                  </div>
                  <span className="text-lg text-muted-foreground">3 credits</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-surface-1 py-8">
        <div className="max-w-[1480px] mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-muted-foreground space-y-2 md:space-y-0">
            <div>
              All rights reserved
            </div>
            <div className="flex space-x-4">
              <button 
                onClick={() => {
                  onShowUpdates();
                  window.history.pushState({}, '', '/updates');
                }}
                className="hover:text-foreground transition-colors"
              >
                Updates
              </button>
              <button 
                onClick={onClose}
                className="hover:text-foreground transition-colors"
              >
                Pricing
              </button>
              <button 
                onClick={() => {
                  onShowTerms();
                  window.history.pushState({}, '', '/terms');
                }}
                className="hover:text-foreground transition-colors"
              >
                Terms
              </button>
              <button 
                onClick={() => {
                  onShowPrivacy();
                  window.history.pushState({}, '', '/privacy');
                }}
                className="hover:text-foreground transition-colors"
              >
                Privacy
              </button>
              <button 
                onClick={onShowCookiePreferences}
                className="hover:text-foreground transition-colors"
              >
                Cookie Settings
              </button>
            </div>
            <div>
              Made with 🍌 by <a href="https://x.com/txh" target="_blank" rel="noopener noreferrer" className="font-medium hover:text-foreground transition-colors">@txh</a>
            </div>
          </div>
        </div>
      </div>

      {/* Credit History Modal */}
      {showCreditHistory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-border rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-semibold text-foreground">
                Credit Purchase History
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    await fetchCreditHistory();
                  }}
                  className="w-8 h-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  title="Refresh history"
                >
                  <History className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowCreditHistory(false)}
                  className="w-8 h-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {/* Debug info */}
              {process.env.NODE_ENV === 'development' && (
                <div className="mb-4 p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded text-xs">
                  <p>Debug: loadingHistory={loadingHistory ? 'true' : 'false'}</p>
                  <p>Debug: creditHistory.length={creditHistory.length}</p>
                  <p>Debug: user.uid={user?.uid}</p>
                </div>
              )}
              
              {loadingHistory ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-3 text-muted-foreground">Loading history...</span>
                </div>
              ) : creditHistory.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No purchase history found.</p>
                  <p className="text-sm text-muted-foreground mt-2">Your credit purchases will appear here after you make a purchase.</p>
                  <p className="text-xs text-muted-foreground mt-4">Check browser console for debugging information.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {creditHistory.map((purchase) => (
                    <div key={purchase.id} className="bg-surface-1 border border-border rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-lg font-semibold text-foreground">
                              {purchase.planName || `${purchase.credits} Credits`}
                            </h4>
                            <Badge className="bg-green-500/20 text-green-600 dark:text-green-400">
                              {purchase.paymentStatus === 'completed' ? 'Completed' : purchase.paymentStatus}
                            </Badge>
                          </div>
                          <div className="space-y-1 text-sm text-muted-foreground">
                            <p>
                              <span className="font-medium text-foreground">Credits:</span> {purchase.credits}
                            </p>
                            <p>
                              <span className="font-medium text-foreground">Amount:</span> {purchase.currency === 'eur' ? '€' : purchase.currency?.toUpperCase() || '€'}{purchase.amount?.toFixed(2) || '0.00'}
                            </p>
                            <p>
                              <span className="font-medium text-foreground">Date:</span> {purchase.purchaseDate?.toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) || 'N/A'}
                            </p>
                            {purchase.sessionId && (
                              <p className="text-xs">
                                <span className="font-medium text-foreground">Transaction ID:</span> {purchase.sessionId.substring(0, 20)}...
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="mt-6 pt-4 border-t border-border">
              <Button
                onClick={() => setShowCreditHistory(false)}
                variant="outline"
                className="w-full"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-border rounded-lg p-6 max-w-md w-full">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Did you share to X?
              </h3>
              <p className="text-muted-foreground mb-6">
                Please confirm that you actually posted the tweet to receive your 5 credits.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    trackEvent('share_for_credits', {
                      completed: false,
                      reason: 'user_denied'
                    });
                    setShowVerificationModal(false);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  No, I didn't share
                </Button>
                <Button
                  onClick={async () => {
                    setShowVerificationModal(false);
                    
                    // Award credits for sharing (only once) and mark as shared
                    try {
                      const success = await addMockups(5);
                      if (success) {
                        // Track successful share completion
                        trackEvent('share_for_credits', {
                          completed: true,
                          credits_earned: 5
                        });
                        
                        // Mark user as having shared in database
                        const userRef = doc(db, 'users', user.uid);
                        await updateDoc(userRef, {
                          hasSharedToX: true,
                          lastActiveAt: new Date()
                        });
                        setHasSharedToX(true);
                        
                        // Show success message
                        const modalData = {
                          credits: 5,
                          headline: "5 credits earned!",
                          text: "Thanks for sharing RandomMockups! Your 5 credits have been added to your account."
                        };
                        setSuccessModalData(modalData);
                        setTimeout(() => {
                          setShowSuccessModal(true);
                        }, 100);
                      }
                    } catch (error) {
                      console.error('Error awarding share credits:', error);
                    }
                  }}
                  className="flex-1"
                >
                  Yes, I shared!
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
